import {
  TradingEventDetailApiResponse,
  TradingUserMetricsApiResponse,
  LeaderboardApiResponse,
  Prize,
  Reward,
  LeaderboardRankRange,
  LeaderboardEntry,
} from '../types/trading-event-api.types';

import { ISingleEvent, ISingleEventLeaderboardItem, ISingleEventRankRange } from '../types';
import { walletAddressEllipsis } from 'utils/ellipsis.utils';
import highlight1 from 'assets/single-event/prize-pool/highlight_1.png';
import highlight2 from 'assets/single-event/prize-pool/highlight_2.png';
import highlight3 from 'assets/single-event/prize-pool/highlight_3.png';

// Metric key to display name mapping
const METRIC_LABELS: Record<string, string> = {
  trade_volume_rmv: 'Trading Volume',
  trade_count: 'NFT Traded',
  fraction_count: 'Amount NFT Traded',
};

// Token currency ID to ticker mapping
const TOKEN_TICKERS: Record<number, string> = {
  1: 'RMV',
  2: 'USDT',
};

const getTokenTicker = (currencyId: number | undefined): string => {
  if (!currencyId) return '';
  return TOKEN_TICKERS[currencyId] || `TOKEN_${currencyId}`;
};

// Format estimated reward for display
// if distribution_type === 'per_person' - use icon_url + name
// else - use icon_url + token_amount (rounded) + TOKEN_TICKER
const formatEstimatedRewardLabel = (prize: Prize): string => {
  if (prize.distribution_type === 'per_person') {
    return prize.name;
  }
  const amount = prize.token_amount ? Math.round(parseFloat(prize.token_amount)).toString() : '';
  const ticker = getTokenTicker(prize.token_currency);
  return `${amount} ${ticker}`.trim();
};

// Format reward for prize pool display (icon_url + label)
const formatRewardLabel = (reward: Reward): string => {
  if (reward.type === 'token' && reward.currency) {
    return `${reward.amount} ${reward.currency}`;
  }
  return reward.label;
};

// Get scoring_metrics from /events/{id}/leaderboard/
const getScoringMetrics = (leaderboard: LeaderboardApiResponse): string[] => {
  if (leaderboard.scoring_metrics && leaderboard.scoring_metrics.length > 0) {
    return leaderboard.scoring_metrics;
  }
  console.warn('scoring_metrics not returned from leaderboard API');
  return ['trade_volume_rmv'];
};

// Map a single leaderboard entry to component format
const mapLeaderboardEntryToItem = (
  entry: LeaderboardEntry,
  scoringMetrics: string[]
): ISingleEventLeaderboardItem => {
  // Username/address display logic:
  // - if username is empty → use formatted wallet address
  // - if username === address (case-insensitive) → use formatted wallet address
  // - else → use username (nickname has top priority)
  const hasNickname =
    !!entry.username && entry.username.toLowerCase() !== entry.address.toLowerCase();
  const displayName = hasNickname ? entry.username! : walletAddressEllipsis(entry.address);

  const item: ISingleEventLeaderboardItem = {
    rank: entry.rank ?? '-',
    uidNickname: displayName,
    isCurrentUser: entry.is_current_user,
  };

  // Add metric columns based on scoring_metrics
  scoringMetrics.forEach((metric) => {
    if (metric === 'trade_volume_rmv') {
      item.totalTradingVolumeUSDT = entry.trade_volume_rmv || '-';
    } else if (metric === 'trade_count') {
      item.nftTraded = entry.trade_count?.toString() || '-';
    } else if (metric === 'fraction_count') {
      item.fractionTraded = entry.fraction_count?.toString() || '-';
    }
  });

  // Add estimated rewards column (same logic as MY STATISTICS but without icons)
  if (entry.estimated_rewards && entry.estimated_rewards.length > 0) {
    const rewardLabels = entry.estimated_rewards.map((r) => formatEstimatedRewardLabel(r.prize));
    item.estimatedRewardUSDT = rewardLabels.join(', ');
  } else {
    item.estimatedRewardUSDT = '-';
  }

  return item;
};

// Map leaderboard_rank_range_data to rank ranges
const mapRankRanges = (rankRangesData: LeaderboardRankRange[]): ISingleEventRankRange[] | null => {
  if (!rankRangesData || rankRangesData.length === 0) return null;

  return rankRangesData.map((range) => {
    // Rank: if rank_from === rank_to -> rank_from, else -> `rank_from - rank_to`
    const rank = range.rank_from === range.rank_to
      ? `${range.rank_from}`
      : `${range.rank_from} - ${range.rank_to}`;

    // Prize Allocation: use rewards_summary (same logic with prize pool but without icons)
    const prizeAllocation =
      (range.rewards_summary || [])
        .map((reward) => {
          if (reward.type === 'token' && reward.currency) {
            return `${reward.amount} ${reward.currency}`;
          }
          return reward.label;
        })
        .join(', ') || '-';

    // Requirements: use metric_key for column header and threshold as value
    // For trade_volume_rmv, use "RMV" so the renderer can show the token icon
    const requirements =
      (range.requirements || [])
        .map((req) => {
          if (req.metric_key === 'trade_volume_rmv') {
            return `${req.threshold} RMV`;
          }
          const label = METRIC_LABELS[req.metric_key] || req.metric_key;
          return `${req.threshold} ${label}`;
        })
        .join(', ') || '-';

    return {
      rank,
      prizeAllocation,
      requirements,
    };
  });
};

const getFeatureHighlightImage = (index: number): string => {
  switch (index) {
    case 1:
      return highlight2;
    case 2:
      return highlight3;
  }
  return highlight1;
};

// Main mapping function: API response -> ISingleEvent
export const mapApiResponseToSingleEvent = (
  eventDetail: TradingEventDetailApiResponse,
  leaderboard: LeaderboardApiResponse,
  userMetrics: TradingUserMetricsApiResponse | null
): ISingleEvent => {
  const scoringMetrics = getScoringMetrics(leaderboard);

  // Prize Pool - use rewards_summary (icon_url + label)
  const prizePool = (eventDetail.rewards_summary || []).map((reward) => ({
    image: reward.icon_url || '',
    name: formatRewardLabel(reward),
  }));

  // Feature Highlights - from feature_highlights
  const featureHighlights = (eventDetail.feature_highlights || []).map((highlight, index) => ({
    image: getFeatureHighlightImage(index),
    name: highlight.label,
    value: highlight.value,
    label: highlight.description,
  }));

  // User statistics - rank + metrics from scoring_metrics + last_updated
  const statistics = scoringMetrics
    .filter((metric) => {
      if (!userMetrics) return false;
      const value = userMetrics[metric as keyof TradingUserMetricsApiResponse];
      return value !== undefined && value !== null;
    })
    .map((metric) => {
      const value = userMetrics?.[metric as keyof TradingUserMetricsApiResponse];
      return {
        name: METRIC_LABELS[metric] || metric,
        value: value?.toString() || '-',
      };
    });

  // Add rank and fraction_count to statistics
  if (userMetrics) {
    // Add fraction_count before other metrics (if not already included via scoring_metrics)
    if (!scoringMetrics.includes('fraction_count')) {
      statistics.unshift({
        name: METRIC_LABELS['fraction_count'],
        value: userMetrics.fraction_count?.toString() || '-',
      });
    }

    if (userMetrics.rank > 0) {
      statistics.unshift({
        name: 'Your Rank',
        value: `#${userMetrics.rank}`,
      });
    }
  }

  // Estimated rewards from /events/{id}/user-metrics/
  const estimatedReward = (userMetrics?.estimated_rewards || []).map((reward) => ({
    image: reward.prize.icon_url,
    name: formatEstimatedRewardLabel(reward.prize),
  }));

  // last_updated timestamp
  const lastUpdated = userMetrics?.last_updated
    ? Math.floor(new Date(userMetrics.last_updated).getTime() / 1000)
    : Math.floor(Date.now() / 1000);

  // Leaderboard items
  const leaderboardItems: ISingleEventLeaderboardItem[] = leaderboard.results.map((entry) => {
    return mapLeaderboardEntryToItem(entry, scoringMetrics);
  });

  // Leaderboard columns based on scoring_metrics
  const leaderboardColumns = [
    { key: 'rank', header: 'RANK' },
    { key: 'uidNickname', header: 'UID NICKNAME' },
  ];

  scoringMetrics.forEach((metric) => {
    if (metric === 'trade_volume_rmv') {
      leaderboardColumns.push({ key: 'totalTradingVolumeUSDT', header: 'TOTAL TRADING VOLUME USDT' });
    } else if (metric === 'trade_count') {
      leaderboardColumns.push({ key: 'nftTraded', header: 'NFT TRADED' });
    } else if (metric === 'fraction_count') {
      leaderboardColumns.push({ key: 'fractionTraded', header: 'FRACTION TRADED' });
    }
  });

  leaderboardColumns.push({ key: 'estimatedRewardUSDT', header: 'ESTIMATED REWARD' });

  // Rank ranges from leaderboard_rank_range_data
  const rankRanges = mapRankRanges(eventDetail.leaderboard_rank_range_data || []);

  // Participation criteria
  const participationCriteria = (eventDetail.participation_criteria || []).map((criteria) => ({
    name: criteria.label,
    value: criteria.description || criteria.value,
  }));

  return {
    id: eventDetail.id.toString(),
    intro: {
      backgroundImage: eventDetail.banner_url || '',
      heroImage: eventDetail.image_url || '',
      iconUrl: eventDetail.icon_url || '',
      title: eventDetail.name,
      subtitle: eventDetail.tagline,
    },
    endAt: Math.floor(new Date(eventDetail.end_datetime).getTime() / 1000),
    stages: [
      {
        id: 1,
        name: 'REGISTRATION STARTS',
        timestamp: Math.floor(new Date(eventDetail.registration_start_datetime).getTime() / 1000),
      },
      {
        id: 2,
        name: 'COMPETITION STARTS',
        timestamp: Math.floor(new Date(eventDetail.start_datetime).getTime() / 1000),
      },
      {
        id: 3,
        name: 'REGISTRATION ENDS',
        timestamp: Math.floor(new Date(eventDetail.registration_end_datetime).getTime() / 1000),
      },
      {
        id: 4,
        name: 'COMPETITION ENDS',
        timestamp: Math.floor(new Date(eventDetail.end_datetime).getTime() / 1000),
      },
      {
        id: 5,
        name: 'REWARDS DISTRIBUTION',
        timestamp: Math.floor(new Date(eventDetail.reward_distribution_date).getTime() / 1000),
        description: 'Within 7 business days after event ends',
      },
    ],
    prizePool,
    featureHighlights,
    myStatistics: {
      statistics,
      lastUpdated,
      estimatedReward,
    },
    leaderboard: {
      title: eventDetail.leaderboard_title || 'EVENT ARENA',
      description: eventDetail.leaderboard_description || '',
      enablePagination: true,
      columns: leaderboardColumns,
      items: leaderboardItems,
      lastUpdated: eventDetail.event_totals?.last_updated || undefined,
    },
    rankRanges: rankRanges
      ? {
        title: eventDetail.leaderboard_ranking_rewards_title || 'Rules for ranking rewards',
        description: eventDetail.leaderboard_ranking_rewards_description || eventDetail.leaderboard_description || '',
        ranges: rankRanges,
      }
      : undefined,
    participationCriteria,
  };
};
